/**
 * ClientFlow premium design tokens.
 *
 * Centralised so the entire app speaks the same visual language as the
 * Dashboard mockup (May 2026). Import these tokens and components into
 * every page — no copy-paste, no drift.
 *
 *   PANEL_CLS   — outer card: emerald-tinted border + soft outer glow
 *   ROW_CLS     — inner row / sub-card (one step less prominent)
 *   PAGE_EYEBROW_CLS — small uppercase "section: …" label above page H1
 *   PAGE_H1_CLS — page heading
 *   PRIMARY_BTN_CLS — green pill primary action with emerald glow
 *
 *   <PageHeader eyebrow="Kunder" title="Kundeoversikt" right={…}/>
 *   <PremiumPanel> wraps a Card with the panel styling.
 *   <StatusPill status="…" /> — Norwegian booking status pills.
 *   <Avatar name="…" size="md" /> — emerald-tinted initials avatar.
 */

import React from "react";
import { Card } from "@/components/ui/card";

// ---------- raw CSS tokens ----------

export const PANEL_CLS =
  "relative rounded-2xl border border-emerald-500/10 bg-card " +
  "ring-1 ring-inset ring-emerald-500/5 " +
  "shadow-[0_2px_30px_-12px_rgba(16,185,129,0.18)] " +
  "dark:shadow-[0_2px_40px_-10px_rgba(16,185,129,0.22)]";

export const ROW_CLS =
  "rounded-xl border border-emerald-500/10 bg-emerald-500/[0.02] hover:bg-emerald-500/[0.06] " +
  "dark:bg-emerald-500/[0.03] dark:hover:bg-emerald-500/[0.07] transition-colors";

export const PAGE_EYEBROW_CLS =
  "text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-500";

export const PAGE_H1_CLS =
  "mt-1.5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight";

export const PAGE_SUB_CLS = "mt-2 text-muted-foreground max-w-2xl";

// Primary action button — emerald pill with soft glow that matches the
// Dashboard topbar "Ny booking". Compose with `Button` from shadcn.
//
//   <Button className={PRIMARY_BTN_CLS}>…</Button>
//
// Same height (`h-10`), same radius (`rounded-2xl`), same emerald glow,
// same icon spacing across every internal admin page. Reuse this for
// "Ny booking", "Ny kunde", "Ny tjeneste", "Ny ansatt", "Opprett kampanje",
// form submit buttons in dialogs, and any "Save / Continue" CTA.
export const PRIMARY_BTN_CLS =
  "rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold " +
  "shadow-[0_8px_30px_-10px_rgba(16,185,129,0.6)] " +
  "h-10 px-4 gap-1.5 inline-flex items-center justify-center transition-colors";

// Secondary / outline action button — same radius, same height, same
// typography, but no fill. Used for "Eksporter bookinger", "Last ned PDF",
// "Tilbake", "Endre" and other supporting actions. Hover lifts to an
// emerald-tinted background so the button still feels part of the
// premium family without competing with the primary CTA.
//
//   <Button variant="outline" className={SECONDARY_BTN_CLS}>…</Button>
//
// (We deliberately keep `variant="outline"` so the underlying shadcn
// `Button` still picks up accessible focus rings, disabled styling, etc.
// SECONDARY_BTN_CLS only overrides the visual layer.)
export const SECONDARY_BTN_CLS =
  "rounded-2xl h-10 px-4 gap-1.5 inline-flex items-center justify-center " +
  "border-emerald-500/20 bg-card hover:bg-emerald-500/[0.07] " +
  "hover:border-emerald-500/30 hover:text-foreground font-medium transition-colors";

// Stable, soft per-staff hue — used in Calendar widget vertical bars and
// the Bookinger page so the same person reads as the same colour
// everywhere. Hash-stable across reloads, sub-set so they're all in the
// emerald-friendly neighbourhood.
export const STAFF_PALETTE = [
  "#10b981", // emerald
  "#0ea5e9", // sky
  "#8b5cf6", // violet
  "#f59e0b", // amber
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f43f5e", // rose
  "#84cc16", // lime
];

export function staffHue(id) {
  if (!id) return "#94a3b8"; // slate for unassigned
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
  return STAFF_PALETTE[Math.abs(h) % STAFF_PALETTE.length];
}

// ---------- components ----------

export function PremiumPanel({ className = "", children, ...rest }) {
  return (
    <Card className={`${PANEL_CLS} ${className}`} {...rest}>
      {children}
    </Card>
  );
}

/**
 * Standard page header — keeps every page top-aligned with the
 * Dashboard's "AI MELDINGER / Automatiske oppfølginger" pattern.
 */
export function PageHeader({ eyebrow, title, subtitle, right, testid }) {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
      data-testid={testid}
    >
      <div className="min-w-0">
        {eyebrow && <p className={PAGE_EYEBROW_CLS}>{eyebrow}</p>}
        <h1 className={PAGE_H1_CLS}>{title}</h1>
        {subtitle && <p className={PAGE_SUB_CLS}>{subtitle}</p>}
      </div>
      {right && <div className="flex flex-wrap items-center gap-2 shrink-0">{right}</div>}
    </div>
  );
}

/**
 * Reads the first letter (or first letters) of a name for the
 * round emerald avatar chip used across the dashboard rows.
 */
export function initials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

const AVATAR_SIZES = {
  sm: "w-8 h-8 text-[11px]",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
};

export function Avatar({ name, size = "md", testid, imageUrl, hue }) {
  const sz = AVATAR_SIZES[size] || AVATAR_SIZES.md;
  // When a `hue` is passed (Calendar / Personal pages) we use it as a
  // tinted ring + bg; otherwise fall back to the brand emerald.
  const inlineStyle = hue
    ? { backgroundColor: `${hue}26`, color: hue, boxShadow: `inset 0 0 0 1px ${hue}55` }
    : undefined;
  return (
    <div
      className={`shrink-0 ${sz} rounded-full overflow-hidden font-semibold flex items-center justify-center ${
        hue ? "" : "ring-1 ring-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      }`}
      style={inlineStyle}
      data-testid={testid}
      title={name || ""}
    >
      {imageUrl
        ? <img src={imageUrl} alt={name || ""} className="w-full h-full object-cover" />
        : <span>{initials(name)}</span>}
    </div>
  );
}

const STATUS_MAP = {
  // Booking statuses (Norwegian)
  booked:    { label: "Bekreftet",   cls: "bg-sky-500/15 text-sky-600 dark:text-sky-300 ring-sky-500/30",       dot: "bg-sky-500" },
  pending:   { label: "Venter svar", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30", dot: "bg-amber-500" },
  completed: { label: "Fullført",    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30", dot: "bg-emerald-500" },
  no_show:   { label: "Ikke møtt",   cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30",     dot: "bg-rose-500" },
  cancelled: { label: "Avlyst",      cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30", dot: "bg-slate-500" },
  // Follow-up / message statuses
  draft:     { label: "Utkast",      cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30", dot: "bg-slate-500" },
  queued:    { label: "I kø",        cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30", dot: "bg-amber-500" },
  sent:      { label: "Sendt",       cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30", dot: "bg-emerald-500" },
  failed:    { label: "Feilet",      cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30",     dot: "bg-rose-500" },
};

export function StatusPill({ status, label, compact = false }) {
  const m = STATUS_MAP[status] || STATUS_MAP.booked;
  const padCls = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span className={`inline-flex items-center gap-1.5 ${padCls} rounded-full font-semibold ring-1 ring-inset ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {label || m.label}
    </span>
  );
}
